import { autobind } from "core-decorators";
import { Inject } from "typescript-ioc";
import { DeliveryChannel } from "./DeliveryChannel";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";
import { Utils } from "Helpers/Scripts/Utils";
import { IChannelUpdateEvent } from "./IChannelUpdateEvent";
import { IAddDeliveryAddressEvent } from "./IAddDeliveryAddressEvent";
import { ISelectedChannelAddress } from "./ISelectedChannelAddress";
import { IMultipleCouriersModel } from "./IMultipleCouriersModel";
import { IDeliveryModel } from "./IDeliveryModel";
import { IMultipleCouriersAddressModel } from "./IMultipleCouriersAddressModel";
import { FetchService } from "Async/Scripts/FetchService";
import { DeliveryNotificationsPhone } from "./DeliveryNotificationsPhone";
import { MultipleCouriersToFriendChannel } from "./MultipleCouriersToFriendChannel";

export class MultipleCouriersDeliveryChannel extends DeliveryChannel {
    private readonly DELIVERY_PAGE: string = "#delivery-page";
    private readonly DELIVERY_PAGE_ID: string = "delivery-page";
    private readonly ADD_NEW_ADDRESS: string = "addnewaddress";
    private readonly EDIT_ADDRESS: string = "editaddress";
    private readonly ADDRESS_LIST_CLASS: string = ".address-list";
    private readonly ADDRESS_ADD_CLASS: string = "js-address-add";
    private readonly ADDRESS_ADD_SELECTOR: string = "." + this.ADDRESS_ADD_CLASS;
    private readonly ADDRESS_EDIT_CLASS: string = "js-address-edit";
    private readonly COURIER_DESCRIPTION_CLASS: string = "channel-details__description";
    private readonly COURIER_IMAGE_CLASS: string = "channel-details__logo";
    private readonly COURIER_DETAILS: string = "channel-details";
    private readonly ADDRESS_EDIT: string = "." + this.ADDRESS_EDIT_CLASS;
    private readonly CHANNEL_CHECKED_CLASS: string = "channel-checked";
    private readonly SRCSET_ATTRIBUTES: string = "&h=20&imageFormat=jpeg 20w";
    private readonly NAME_SPACE: string = "homedeliverychannel";
    private readonly COURIERS_ADDRESS_ITEM_TEMPLATE: string = "multiple-couriers-address-item";
    private readonly MULTIPLE_COURIERS_STOCK_INFO_TEMPLATE: string =
        "multiple-couriers-info-template";
    private readonly MULTIPLE_COURIERS_DELIVERY_DATE_TEMPLATE: string =
        "multiple-couriers-delivery-date-template";
    private readonly HOME_ADDRESS_DATA_KEY: string = "homeAddress";
    private readonly KENDO_SELECTED_CLASS: string = "k-state-selected";
    private readonly SELECTED_CLASS: string = "selected";
    private readonly DISPLAY_ATTRIBUTE: string = "display";
    private readonly ADDRESS_BUTTONS: string = "input[name='multipleCouriersAddressRadio']";
    private readonly SELECTED_ADDRESS_BUTTON: string = this.ADDRESS_BUTTONS + ":checked";
    private readonly ADDRESS_BUTTON_ID_PREFIX: string = "#" + this.DELIVERY_ADDRESS_PREFIX;
    private readonly METHOD_RADIO_ID: string = "multipleCouriersChannel";
    private readonly METHOD_RADIO_SELECTOR: string = "#" + this.METHOD_RADIO_ID;
    private readonly METHOD_LABEL_ID = "multipleCouriersLabel";
    private readonly COURIER_WRAPPER: string = ".courier-wrapper";
    private readonly VISIBLE_COURIERS: string = this.COURIER_WRAPPER + ":visible";
    private readonly SELECTED_COURIER: string = this.COURIER_WRAPPER + "." + this.SELECTED_CLASS;
    private readonly RADIO_DESCRIPTION_WITH_CHANNELS: string = ".radio-description";
    private readonly DELIVERY_COMMENT: string = "#delivery-comment";
    private readonly DELIVERY_COMMENT_WRAPPER_SELECTOR: string = ".js-delivery-comment";
    private readonly DELIVERY_DATE: string = "js-delivery-date";
    private readonly ADDRESS_ITEM: string = ".js-address-item";
    private readonly UPDATE_DELIVERY_COMMENT_DATA_KEY: string = "updateDeliveryCommentUrl";
    private readonly DELETE_DELIVERY_ADDRESS_DATA_KEY: string = "deleteDeliveryAddressUrl";
    private readonly ADDRESS_DATA_KEY: string = "address";
    private readonly ADDRESSES_WRAPPER_SELECTOR: string = ".js-multiple-couriers-addresses";
    private readonly ADDRESS_RADIO_SELECTOR: string = "[id^='delivery-address']";
    private readonly ACTIVE_SHOWN_DATA_ATTRIBUTE: string = "data-active-shown";
    private readonly ACTIVE_HIDDEN_DATA_ATTRIBUTE: string = "data-active-hidden";
    private readonly ENABLED_SELECTOR: string = ":enabled";
    private readonly INPUT_SELECTOR: string = "input";
    private readonly ADDRESS_DELETE_DIALOG_ID: string = "AddressDeleteDialog";
    private readonly ADDRESS_DELETE_DIALOG: string = "#" + this.ADDRESS_DELETE_DIALOG_ID;
    private readonly ADDRESS_DELETE_DIALOG_CLASS: string = "address-delete-popup";
    private readonly ADDRESS_DELETE_TEMPLATE_ID: string = "kendo-template-address-delete";
    private readonly SEL_CLOSE_ADDRESS_DELETE_DIALOG: string = ".js-close-address-delete-dialog";
    private readonly SEL_ADDRESS_DELETE_ACTION: string = ".js-address-delete";
    private readonly SEL_ADDRESS_DELETE_SUBMIT: string = ".js-address-delete-submit";
    private readonly SEL_WINDOW: string = ".k-window";
    private readonly COURIERS_LIST_SELECTOR: string = ".js-couriers-list";
    private readonly COURIER_TEMPLATE_ID: string = "multiple-couriers-channel-item";
    private _addressDeleteDialogTitle: string;
    private _isAddressDeleteDialogInited: boolean;
    private _addressDeleteDialog: kendo.ui.Dialog;
    private _selectedDeliveryAddressId: number | null = null;
    private _updateDeliveryCommentUrl: string;
    private _deleteDeliveryAddressUrl: string;
    private _multipleCouriersFriendComponent: MultipleCouriersToFriendChannel | undefined;

    private readonly _ajaxErrorTitle: string = "Error";
    private readonly _ajaxErrorMessage: string =
        "An error occured during request, please try again later.";

    private readonly _fetchService: FetchService;
    protected _model: IMultipleCouriersModel;
    private _deliveryNotificationsPhone: DeliveryNotificationsPhone;

    public static readonly templateId: string = "multiple-couriers-template";

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory,
        @Inject fetchService: FetchService
    ) {
        super(componentFactory, binder, loggerFactory);

        this._fetchService = fetchService;
    }

    public get key(): string {
        return "MultipleCouriersDeliveryChannel";
    }

    private get _addNewAddressTemplate(): (data: {}) => string {
        return Utils.getTemplate(this.COURIERS_ADDRESS_ITEM_TEMPLATE);
    }

    private get _multipleCouriersStockInfoTemplate(): (data: {}) => string {
        return Utils.getTemplate(this.MULTIPLE_COURIERS_STOCK_INFO_TEMPLATE);
    }

    private get _multipleCouriersDeliveryDateTemplate(): (data: IChannelsInfo) => string {
        return Utils.getTemplate(this.MULTIPLE_COURIERS_DELIVERY_DATE_TEMPLATE);
    }

    private get _multipleCouriersChannelItemTemplate(): (data: {}) => string {
        return Utils.getTemplate(this.COURIER_TEMPLATE_ID);
    }

    private get _isAddAddressByHomeLabelAllowed(): boolean {
        return (
            !this._model.CourierHomeDeliveryAddresses.length &&
            this._model.IsAddHomeDeliveryAddressAllowed
        );
    }

    public init(): void {
        this._setOptions();
        this._initRadioButtons();
        this._disableEmptyAddresses();
        this._enableAllowedAddresses();
        this._hideNotAllowedChannels();

        if (
            !this._model.isDefaultSelected &&
            this._model.SelectedHomeDeliveryAddress &&
            this._model.SelectedDeliveryChannel &&
            this._containsSelected()
        ) {
            this._makeCourierSelected();
            this._makeAddressSelected(this._model.SelectedHomeDeliveryAddress);
            this._makeHomeDeliverySelected();
            this._toggleFeesVisibility(this._model.SelectedHomeDeliveryAddress, false);
        }
        this._initDeliveryComment();
        this._bindEvents();
        this._initAllOutOfStockTooltips();
        this._setUpdateDeliveryCommentUrl();
        this._setDeleteDeliveryAddressUrl();
        if (
            this._model.HomeDeliveryPhoneNumbers &&
            this._model.HomeDeliveryPhoneNumbers.NotificationsPhones
        ) {
            this._deliveryNotificationsPhone = this.createComponent(
                DeliveryNotificationsPhone,
                this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR,
                this._model.HomeDeliveryPhoneNumbers
            );
            const phoneBox = this.findElement(this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR);
            if (phoneBox) {
                Utils.removeClass(phoneBox, this.CLASS_HIDDEN);
            }
        }
        this._forceDisplayInvalidAddresses(this._model.CourierHomeDeliveryAddresses);
    }

    private _setOptions(): this {
        this._logger.info("Setting delivery options from data attributes");
        const page = document.getElementById(this.DELIVERY_PAGE_ID);
        if (page) {
            const addressDeleteDialogTitleData = page.getAttribute(
                "data-address-delete-dialog-title"
            );
            if (addressDeleteDialogTitleData) {
                this._addressDeleteDialogTitle = addressDeleteDialogTitleData.toString();
            }
        }
        return this;
    }

    @autobind
    private _handleComponentRefresh(): void {
        if (!this._model.SelectedHomeDeliveryAddress) {
            return;
        }
        this._makeCourierSelected();
        this._makeAddressSelected(this._model.SelectedHomeDeliveryAddress);
        this._makeHomeDeliverySelected();
        this._toggleFeesVisibility(this._model.SelectedHomeDeliveryAddress, false);
    }

    private _setDeleteDeliveryAddressUrl(): void {
        const page = this.closest(this.DELIVERY_PAGE);
        if (page) {
            this._deleteDeliveryAddressUrl = Utils.getData(
                page,
                this.DELETE_DELIVERY_ADDRESS_DATA_KEY
            ).deleteDeliveryAddressUrl;
        }
    }

    private _setUpdateDeliveryCommentUrl(): void {
        const page = this.closest(this.DELIVERY_PAGE);
        if (page) {
            this._updateDeliveryCommentUrl = Utils.getData(
                page,
                this.UPDATE_DELIVERY_COMMENT_DATA_KEY
            ).updateDeliveryCommentUrl;
        }
    }

    private _initAllOutOfStockTooltips(): void {
        for (const address of this._model.CourierHomeDeliveryAddresses) {
            this._initOutOfStockTooltips(address);
        }
    }

    private _initOutOfStockTooltips(address: IMultipleCouriersAddressModel): void {
        const homeAddressSelector = `[${this.DATA_ADDRESS}="${address.Id}"]`;
        this._handleOutOfStockTooltip(address.OutOfStockItems, 0, homeAddressSelector);
        this._handleUnsupportedAddressTooltip(0, homeAddressSelector);
    }

    private _makeHomeDeliverySelected(): void {
        this._makeChannelSelected();
    }

    private _makeCourierSelected(deliveryChannel?: number): void {
        const selectedDeliveryChannel = deliveryChannel
            ? deliveryChannel
            : this._model.SelectedDeliveryChannel;

        const courier = this.findElement(
            `li[${this.DATA_ADDRESS}="${this._model.SelectedHomeDeliveryAddress}"]
                [${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${selectedDeliveryChannel}"]`
        );
        if (courier && !Utils.hasClass(courier, this.CLASS_HIDDEN)) {
            Utils.addClass(courier, this.SELECTED_CLASS);
        }
    }

    private _makeAddressSelected(addressId: number): void {
        const addressButton = this.findElement(`input[value="${addressId}"]`);
        if (addressButton && !addressButton.hasAttribute(this.DISABLED_ATTRIBUTE)) {
            const radioButton = kendo.getRadioButton(addressButton);
            if (radioButton) {
                radioButton.value(true);
                this._selectedDeliveryAddressId = addressId;
            }
        }
    }

    private _containsSelected(): boolean {
        return this._model.CourierHomeDeliveryAddresses.some(
            (address) =>
                address.Id === this._model.SelectedHomeDeliveryAddress &&
                address.HomeDeliveryChannels.some(
                    (channel) => channel.DeliveryChannelId === this._model.SelectedDeliveryChannel
                )
        );
    }

    private handleFriendDeliveryUpdate(nextState: IMultipleCouriersModel): void {
        if (
            this._multipleCouriersFriendComponent &&
            this._multipleCouriersFriendComponent !== nextState.friendDeliveryChannel
        ) {
            this._multipleCouriersFriendComponent.unbindSelectCourierChannel(this.key);
            this._multipleCouriersFriendComponent = undefined;
        }

        if (nextState.friendDeliveryChannel) {
            this._multipleCouriersFriendComponent = nextState.friendDeliveryChannel;
            this._multipleCouriersFriendComponent.bindSelectCourierChannel(
                this._handleComponentRefresh,
                this.key
            );
        }
    }

    public update(nextState: IMultipleCouriersModel): void {
        this.handleFriendDeliveryUpdate(nextState);

        if (
            this._model.CourierHomeDeliveryAddresses.length <
            nextState.CourierHomeDeliveryAddresses.length
        ) {
            this._updateAddedAddressesFromModel(nextState);
        }

        let wasComponentUpdated = false;

        for (let i = 0; i < this._model.CourierHomeDeliveryAddresses.length; i++) {
            const previousAddress = this._model.CourierHomeDeliveryAddresses[i];
            const nextAddress = nextState.CourierHomeDeliveryAddresses[i];

            if (!nextAddress) {
                continue;
            }

            if (nextAddress && previousAddress) {
                if (this._isEndCustomerFirstCheckout()) {
                    this._updateDeliveryAddress(nextAddress);

                    if (nextState.SelectedHomeDeliveryAddress) {
                        this._selectedDeliveryAddressId = nextState.SelectedHomeDeliveryAddress;
                    }

                    let selectedDeliveryChannel;
                    if (nextState.SelectedDeliveryChannel) {
                        selectedDeliveryChannel = nextState.SelectedDeliveryChannel;
                    }
                    this._makeCourierSelected(selectedDeliveryChannel);
                    wasComponentUpdated = true;
                }

                const allowedNextAddressChannels = nextAddress.HomeDeliveryChannels.filter(
                    (channel) => channel.IsAllowed
                );

                this._removePreviousExcessCouriers(
                    previousAddress.HomeDeliveryChannels,
                    allowedNextAddressChannels,
                    previousAddress.Id
                );
                this._addNextMissingCouriers(
                    previousAddress.HomeDeliveryChannels,
                    allowedNextAddressChannels,
                    nextAddress.Id,
                    nextState.Message
                );

                const minimalCouriersLength = Math.min(
                    previousAddress.HomeDeliveryChannels.length,
                    allowedNextAddressChannels.length
                );

                for (let j = 0; j < minimalCouriersLength; j++) {
                    const previousChannel = previousAddress.HomeDeliveryChannels[j];
                    const nextChannel = allowedNextAddressChannels[j];

                    const wasChannelUpdated = this._isUpdatedFromDeliveryModel(
                        previousChannel,
                        nextChannel,
                        nextAddress.Id
                    );
                    if (wasChannelUpdated) {
                        wasComponentUpdated = true;
                    }
                    if (nextState.SelectedDeliveryChannel === nextChannel.DeliveryChannelId) {
                        nextAddress.OutOfStockItems = nextChannel.OutOfStockItems;
                        this._updateStockInfo(nextAddress);
                    }
                }

                if (this._isHomeDeliverySelected()) {
                    this._selectNextSelectedChannel(
                        allowedNextAddressChannels,
                        nextState.SelectedDeliveryChannel
                    );
                }

                const checkedChannels = document.querySelectorAll(`.${this.CHANNEL_CHECKED_CLASS}`);
                checkedChannels.forEach((channel) => {
                    channel.classList.remove(`${this.CHANNEL_CHECKED_CLASS}`);
                });
            }
        }

        this._toggleFeesVisibility(this._selectedDeliveryAddressId, false);

        if (
            !this._model.CourierHomeDeliveryAddresses.length &&
            nextState.CourierHomeDeliveryAddresses.length === 1
        ) {
            const radio = this.findElement(`#${this.METHOD_RADIO_ID}`);
            if (!radio) {
                return;
            }
            const kendoRadio = kendo.getRadioButton(radio);
            kendoRadio?.enable(true);
            kendoRadio?.value(true);
        }

        this._model = nextState;

        if (wasComponentUpdated) {
            this._disableEmptyAddresses();
            this._enableAllowedAddresses();
            this._hideNotAllowedChannels();
            this._forceDisplayInvalidAddresses(this._model.CourierHomeDeliveryAddresses);
        }
    }

    private _selectNextSelectedChannel(
        nextChannels: IChannelsInfo[],
        selectedDeliveryChannelId: number | null
    ) {
        const currentSelectedChannel = this.findElement(this.SELECTED_COURIER);

        if (currentSelectedChannel) {
            const currentSelectedChannelId = Utils.getAttr(
                currentSelectedChannel,
                this.DATA_DELIVERY_CHANNEL
            );
            if (
                currentSelectedChannelId &&
                selectedDeliveryChannelId &&
                Number(currentSelectedChannelId) === selectedDeliveryChannelId
            ) {
                return;
            }
        }

        this._removePreviousCourierSelection();

        const nextSelectedChannel = nextChannels.find(
            (nextChannel) => nextChannel.DeliveryChannelId === selectedDeliveryChannelId
        );
        if (nextSelectedChannel) {
            this._makeCourierSelected(nextSelectedChannel.DeliveryChannelId);
        } else if (this._selectedDeliveryAddressId) {
            this._selectFirstCourier(this._selectedDeliveryAddressId);
        }
    }

    private _removePreviousExcessCouriers(
        previousChannels: IChannelsInfo[],
        nextChannels: IChannelsInfo[],
        previousAddressId: number
    ) {
        if (nextChannels.length >= previousChannels.length) {
            return;
        }

        const excessCouriers = previousChannels.slice(nextChannels.length);

        for (const excessCourier of excessCouriers) {
            this.findElementStrict(
                `${this.COURIER_WRAPPER}[${this.DATA_ADDRESS}='${previousAddressId}'][${this.DATA_DELIVERY_CHANNEL}='${excessCourier.DeliveryChannelId}']`
            ).remove();
        }
    }

    private _addNextMissingCouriers(
        previousChannels: IChannelsInfo[],
        nextChannels: IChannelsInfo[],
        addressId: number,
        message: IMessage | null
    ): void {
        if (nextChannels.length <= previousChannels.length) {
            return;
        }

        const missingCouriers = nextChannels.slice(previousChannels.length);

        const couriersList = this.findElementStrict(
            `li[${this.DATA_ADDRESS}="${addressId}"] ${this.COURIERS_LIST_SELECTOR}`
        );

        for (const missingCourier of missingCouriers) {
            const data = {
                AddressId: addressId,
                channel: missingCourier,
                DeliveryChannelId: missingCourier.DeliveryChannelId,
                DeliveryDate: missingCourier.DeliveryDate,
                DeliveryFee: missingCourier.DeliveryFee,
                Description: missingCourier.Description,
                Message: message,
            };

            const listItem = Utils.createDomElementFromMarkup(
                this._multipleCouriersChannelItemTemplate(data)
            );
            Utils.append(couriersList, listItem);
        }
    }

    private _updateAddedAddressesFromModel(nextState: IMultipleCouriersModel): void {
        const addedAddresses = nextState.CourierHomeDeliveryAddresses.slice(
            this._model.CourierHomeDeliveryAddresses.length
        );

        for (const address of addedAddresses) {
            this._updateStockInfo(address);
            for (const channel of address.HomeDeliveryChannels) {
                this._updateDeliveryDate(channel, address.Id);
                this._updateCourierFee(channel.DeliveryFee, channel.DeliveryChannelId, address.Id);
            }
        }
    }

    private _isUpdatedFromDeliveryModel(
        prevState: IChannelsInfo,
        nextState: IChannelsInfo,
        addressId: number
    ): boolean {
        if (!this._shouldComponentUpdate(prevState, nextState)) {
            const courier = document.querySelector(
                `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']`
            );
            if (courier) {
                courier.classList.add(this.CHANNEL_CHECKED_CLASS);
            }
            return false;
        }

        this._checkCourier(nextState, addressId);

        return true;
    }

    private _updateStockInfo(address: IMultipleCouriersAddressModel): void {
        const homeAddressSelector = `[${this.DATA_ADDRESS} = "${address.Id}"]`;
        const stockInfo = this.findElement(
            homeAddressSelector + ` .${this.DELIVERY_CHANNEL_INFO_CLASS}`
        );

        if (stockInfo) {
            const data = {
                address,
            };
            Utils.setHtml(stockInfo, this._multipleCouriersStockInfoTemplate(data));
            this._handleOutOfStockTooltip(address.OutOfStockItems, 0, homeAddressSelector);
            this._handleUnsupportedAddressTooltip(0, homeAddressSelector);
        }
    }

    private _updateDeliveryDate(data: IChannelsInfo, addressId: number): void {
        const deliveryDate = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${data.DeliveryChannelId}"][${this.DATA_ADDRESS}="${addressId}"]
                    .${this.DELIVERY_DATE}`
        );

        if (deliveryDate) {
            Utils.setHtml(deliveryDate, this._multipleCouriersDeliveryDateTemplate(data));
        }
    }

    private _updateCourierFee(fee: number, deliveryChannelId: number, addressId: number): void {
        const deliveryFee = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${deliveryChannelId}"][${this.DATA_ADDRESS}="${addressId}"]
                    .${this.DELIVERY_FEE_CLASS}`
        );
        if (deliveryFee) {
            Utils.setHtml(deliveryFee, kendo.toString(fee, "oc"));
        }
    }

    private _checkCourier(nextState: IChannelsInfo, addressId: number): void {
        const courier = document.querySelector(
            `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']`
        );

        if (courier) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const courierId = (courier as HTMLElement).dataset["deliveryChannel"];

            if (nextState.IsAllowed) {
                Utils.toggleClass(courier, this.CLASS_HIDDEN, !nextState.IsAllowed);
            }

            const courierDescription = document.querySelector(
                `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                 .${this.COURIER_DESCRIPTION_CLASS}`
            );

            if (courierDescription) {
                Utils.setHtml(courierDescription, kendo.toString(nextState.Description, "oc"));
            }

            let courierImage = document.querySelector(
                `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}=
                '${addressId}'][${this.DATA_DELIVERY_CHANNEL}='${courierId}'] .${this.COURIER_IMAGE_CLASS}`
            );

            const courierImagePath = document.querySelector(
                `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                 .${this.COURIER_DETAILS}`
            );

            if (nextState.Image) {
                if (!courierImage && courierImagePath) {
                    courierImage = document.createElement("img");
                    courierImage.classList.add(this.COURIER_IMAGE_CLASS);
                    courierImagePath.appendChild(courierImage);
                }

                if (courierImage) {
                    courierImage.setAttribute(
                        "srcset",
                        nextState.Image.Src + this.SRCSET_ATTRIBUTES
                    );
                    courierImage.setAttribute("alt", nextState.Image.Alt);
                }
            } else {
                if (courierImage) {
                    Utils.addClass(courierImage, this.CLASS_HIDDEN);
                }
            }

            if (nextState.DeliveryDate) {
                const deliveryDate = document.querySelector(
                    `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                     .${this.DELIVERY_DATE}`
                );

                if (deliveryDate) {
                    Utils.setHtml(
                        deliveryDate,
                        this._multipleCouriersDeliveryDateTemplate(nextState)
                    );
                }
            }

            const deliveryFee = document.querySelector(
                `${this.COURIER_WRAPPER}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                 .${this.DELIVERY_FEE_CLASS}`
            );

            if (deliveryFee) {
                Utils.setHtml(deliveryFee, kendo.toString(nextState.DeliveryFee, "oc"));
            }

            const courierNextId = nextState.DeliveryChannelId.toString();
            courier.classList.add(this.CHANNEL_CHECKED_CLASS);
            courier.setAttribute(this.DELIVERY_CHANNEL_DATA_ATTRIBUTE, courierNextId);
        }
    }

    private _updateDeliveryAddress(nextAddress: IAddress): void {
        const addressItem = this.findElement(
            `${this.ADDRESSES_WRAPPER_SELECTOR} ${this.ADDRESS_ITEM}`
        );

        if (!addressItem) {
            this._logger.error("Address item " + this.ADDRESS_ITEM + " was not found.");
            return;
        }

        const addressToRemoveRadioElement = addressItem.querySelector(this.ADDRESS_RADIO_SELECTOR);

        if (!addressToRemoveRadioElement) {
            return;
        }

        const wasRadioSelected = Utils.isChecked(addressToRemoveRadioElement);

        if (wasRadioSelected) {
            return;
        }

        const data = {
            AddressId: nextAddress.Id,
            DeliveryChannelId: this._model.DeliveryChannelId,
            address: nextAddress,
        };

        const updatedAddressString = this._addNewAddressTemplate(data);
        const updatedAddressMarkup = Utils.createDomElementFromMarkup(updatedAddressString);

        /* the bug #790909 was perorted in scope of SCR feature.
         * in order not to destroy other functionality,
         * there was condition added
         */

        if (!ori.shopping?.wizard?.isSmoothRegistration()) {
            Utils.replaceWith(addressItem, updatedAddressMarkup);
        }

        const updatedAddressSelector = `[value="${nextAddress.Id}"]`;
        const updatedAddressRadio = updatedAddressMarkup.querySelector(updatedAddressSelector);
        if (updatedAddressRadio) {
            const radioData = kendo.createRadioButton(updatedAddressRadio);
            if (wasRadioSelected && nextAddress.isAllowed && radioData) {
                radioData.value(true);
            }
        }
    }

    private _bindEvents(): void {
        this.bindClick(this._handleClick);
        this._bindAddNewAddress();
        this._bindEditAddress();
        this._bindStepChange();
        this._bindChannelChange();
        this._bindCommentChange();
        this._bindAddressDeleteClick();
    }

    private _bindCommentChange(): void {
        const textBox = kendo.getTextBox(this.DELIVERY_COMMENT);
        if (textBox) {
            textBox.bindChange(this._updateDeliveryComment);
        }
    }

    private _bindChannelChange(): void {
        const input = this.findElement(this.METHOD_RADIO_SELECTOR);
        if (input) {
            const radioButton = kendo.getRadioButton(input);
            if (radioButton) {
                radioButton.bindChange((event) => {
                    if (event && event.sender) {
                        this._removeAddressSelection();
                        this._triggerUpdateChannel(event.sender);
                    }
                });
            }
        }
    }

    @autobind
    private _triggerUpdateChannel(homeDeliveryRadio: kendo.ui.RadioButton): void {
        if (homeDeliveryRadio.value()) {
            const data: ISelectedChannelAddress = {
                deliveryAddress: null,
                deliveryChannel: null,
            };

            this._triggerChannelUpdate(data);
        }
    }

    @autobind
    private _removeAddressSelection(): void {
        this._removePreviousCourierSelection();
        this._deselectAddress();
    }

    private _deselectAddress(): void {
        const addressButton = this.findElement(this.SELECTED_ADDRESS_BUTTON);
        if (addressButton) {
            const radioButton = kendo.getRadioButton(addressButton);
            if (radioButton) {
                radioButton.value(false);
                radioButton.wrapper.removeClass(this.KENDO_SELECTED_CLASS);
                radioButton.wrapper
                    .siblings(this.RADIO_DESCRIPTION_WITH_CHANNELS)
                    .css(this.DISPLAY_ATTRIBUTE, "");
            }
        }
    }

    @autobind
    private _handleClick(event: IChannelUpdateEvent): void {
        if (Utils.hasClass(event.target, this.ADDRESS_ADD_CLASS)) {
            if (ori.shopping && ori.shopping.customer) {
                ori.shopping.customer.addAddressFormDialog();
            }

            return;
        }

        if (Utils.hasClass(event.target, this.ADDRESS_EDIT_CLASS)) {
            if (ori.shopping && ori.shopping.customer) {
                const address = { addressId: this._selectedDeliveryAddressId };
                ori.shopping.customer.editAddressFormDialog(address);
            }
            return;
        }

        if (event.target.id === this.METHOD_LABEL_ID) {
            if (this._isHomeDeliveryEnabled() && this._isAddAddressByHomeLabelAllowed) {
                event.preventDefault();

                if (ori.shopping && ori.shopping.customer) {
                    ori.shopping.customer.addAddressFormDialog();
                }
                return;
            }
        }

        if (event.target.id === this.METHOD_RADIO_ID) {
            if (
                this._model.HomeDeliveryPhoneNumbers &&
                !this._model.HomeDeliveryPhoneNumbers.SelectedNotificationsPhoneId &&
                this._deliveryNotificationsPhone
            ) {
                this._deliveryNotificationsPhone.clearPhoneDropDownListSelection();
            }

            const enabledAddress = this.findElement(
                `${this.ADDRESS_ITEM} ${this.INPUT_SELECTOR}${this.ENABLED_SELECTOR}`
            );
            if (enabledAddress) {
                const enabledAddressId = Number((enabledAddress as HTMLInputElement).value);
                this._selectAddress(enabledAddressId);
            }
        }

        if (Utils.getData(event.target, this.HOME_ADDRESS_DATA_KEY)) {
            const nextAddressId = Number((event.target as HTMLInputElement).value);
            this._selectAddress(nextAddressId);
        }

        const clickedCourier = this._getClickedCourier(event.target);

        if (clickedCourier && !Utils.getAttr(clickedCourier, this.DISABLED_ATTRIBUTE)) {
            const data: ISelectedChannelAddress = {
                deliveryAddress: null,
                deliveryChannel: null,
                isCourierSelection: true,
                preventReloadModules: false,
            };

            this._handleCourierClick(clickedCourier, data);
        }
    }

    private _handleCourierClick(clickedCourier: Element, data: ISelectedChannelAddress): void {
        const deliveryChannelId = Utils.getAttr(clickedCourier, this.DATA_DELIVERY_CHANNEL);

        if (deliveryChannelId) {
            data.deliveryChannel = Number(deliveryChannelId);
            data.deliveryAddress = this._selectedDeliveryAddressId;
            this._removePreviousCourierSelection();
            Utils.addClass(clickedCourier, this.SELECTED_CLASS);
            if (data.deliveryAddress !== null) {
                this._triggerChannelUpdate(data);
            }
        }
    }

    private _handleAddressClick(nextAddressId: number, data: ISelectedChannelAddress): void {
        if (
            this._selectedDeliveryAddressId !== nextAddressId ||
            this._model.SelectedHomeDeliveryAddress !== nextAddressId
        ) {
            this._toggleFeesVisibility(this._selectedDeliveryAddressId, true);
            this._selectedDeliveryAddressId = nextAddressId;
            data.deliveryAddress = this._selectedDeliveryAddressId;
            this._removePreviousCourierSelection();
            this._triggerChannelUpdate(data);
        }
    }

    private _selectFirstCourier(addressId: number): void {
        const firstVisibleCourier = this.findElement(
            `[${this.DATA_ADDRESS}="${addressId}"] ` + this.COURIER_WRAPPER
        );

        const data: ISelectedChannelAddress = {
            deliveryAddress: null,
            deliveryChannel: null,
            isCourierSelection: true,
            preventReloadModules: false,
        };

        if (firstVisibleCourier) {
            this._handleCourierClick(firstVisibleCourier, data);
        } else {
            data.deliveryAddress = this._selectedDeliveryAddressId;
            this._triggerChannelUpdate(data);
        }
    }

    private _selectAddress(addressId: number): void {
        const data: ISelectedChannelAddress = {
            deliveryAddress: null,
            deliveryChannel: null,
            isCourierSelection: true,
            preventReloadModules: false,
        };
        this._handleAddressClick(addressId, data);
        this._makeAddressSelected(addressId);
        this._selectFirstCourier(addressId);
    }

    private _removePreviousCourierSelection(): void {
        const previousCourier = this.findElement(this.SELECTED_COURIER);
        if (previousCourier) {
            Utils.removeClass(previousCourier, this.SELECTED_CLASS);
        }
    }

    private _getClickedCourier(innerElement: Element): Element | null {
        const couriers = this.find(this.VISIBLE_COURIERS);
        for (const courier of couriers) {
            if (courier.contains(innerElement)) {
                return courier;
            }
        }
        return null;
    }

    private _bindAddNewAddress(): void {
        this._binder.bindCustomEvent(
            this.ADD_NEW_ADDRESS,
            (event: IAddDeliveryAddressEvent) => {
                event.stopPropagation();
                this.addNewAddress(event.lastAddedAddress, event.deliveryModel);
            },
            this.NAME_SPACE
        );
    }

    private _bindEditAddress(): void {
        this._binder.bindCustomEvent(
            this.EDIT_ADDRESS,
            (event: IAddDeliveryAddressEvent) => {
                event.stopPropagation();
                this.editAddress(event.editedAddress, event.deliveryModel);
            },
            this.NAME_SPACE
        );
    }

    private _bindStepChange(): void {
        if (!ori) {
            this._logger.error("Ori namespace is not available");
            return;
        }

        if (!ori.shopping || !ori.shopping.wizard) {
            this._logger.error("Shopping wizard is not available");
            return;
        }
        ori.shopping.wizard.on(ori.shopping.wizard.shoppingContextChangeEvent(), () => {
            this._toggleEditButton();
            this._updateDeliveryComment();
        });
    }

    private _toggleEditButton(): void {
        const editButtons = this.find(this.ADDRESS_EDIT);
        if (editButtons) {
            editButtons.forEach((button) =>
                Utils.toggleClass(
                    button,
                    this.CLASS_HIDDEN,
                    this._selectedDeliveryAddressId === null
                )
            );
        }
    }

    public addNewAddress(lastAddress: IAddress, deliveryModel: IDeliveryModel): void {
        const addressList = this.findElement(this.ADDRESS_LIST_CLASS);
        if (!addressList) {
            this._logger.error(`Address list ${this.ADDRESS_LIST_CLASS} was not found.`);
            return;
        }
        const data = {
            IsDeleteHomeDeliveryAddressAllowed: deliveryModel.IsDeleteHomeDeliveryAddressAllowed,
            IsEditHomeDeliveryAddressAllowed: deliveryModel.IsEditHomeDeliveryAddressAllowed,
            address: this._prepareAddressData(lastAddress, deliveryModel),
        };

        if (data.address) {
            const listItem = Utils.createDomElementFromMarkup(this._addNewAddressTemplate(data));

            Utils.append(addressList, listItem);

            const addressRadioButton = this._initRadioButton(
                `${this.ADDRESS_BUTTON_ID_PREFIX}${lastAddress.Id}`
            );

            this._initOutOfStockTooltips(data.address);

            for (const channel of data.address.HomeDeliveryChannels) {
                this._toggleChannelDisplay(channel, data.address.Id);
            }

            if (addressRadioButton) {
                const isLastAddressAllowed = Boolean(lastAddress.isAllowed);
                this._enableAllowedAddress(
                    addressRadioButton.element[0] as HTMLInputElement,
                    isLastAddressAllowed
                );
                this._disableEmptyAddresses();
                this._toggleAddAddressButtonVisibility(
                    deliveryModel.IsAddHomeDeliveryAddressAllowed
                );

                if (isLastAddressAllowed) {
                    const addressId = Number(addressRadioButton.element.val());
                    if (!isNaN(addressId)) {
                        this._selectAddress(addressId);
                    }
                    const allAddresses = [...this._model.CourierHomeDeliveryAddresses];
                    allAddresses.push(data.address);
                    this._forceDisplayInvalidAddresses(allAddresses);
                    this._makeHomeDeliverySelected();
                }
            }
        }
    }

    public editAddress(editedAddress: IAddress, deliveryModel: IDeliveryModel): void {
        const addressList = this.findElement(this.ADDRESS_LIST_CLASS);
        if (!addressList) {
            this._logger.error(`Address list ${this.ADDRESS_LIST_CLASS} was not found.`);
            return;
        }

        const radioSelector = `[value="${this._selectedDeliveryAddressId}"]`;
        const addressToRemoveRadio = addressList.querySelector(radioSelector);

        if (!addressToRemoveRadio) {
            return;
        }

        const data = {
            IsDeleteHomeDeliveryAddressAllowed: deliveryModel.IsDeleteHomeDeliveryAddressAllowed,
            IsEditHomeDeliveryAddressAllowed: deliveryModel.IsEditHomeDeliveryAddressAllowed,
            address: this._prepareAddressData(editedAddress, deliveryModel),
        };

        const addressToRemove = Utils.closest(addressToRemoveRadio, this.ADDRESS_ITEM);
        if (addressToRemove) {
            const updatedAddress = this._addNewAddressTemplate(data);

            Utils.replaceWith(addressToRemove, updatedAddress);
            this._selectedDeliveryAddressId = editedAddress.Id;

            if (data.address) {
                this._initOutOfStockTooltips(data.address);

                for (const channel of data.address.HomeDeliveryChannels) {
                    this._toggleChannelDisplay(channel, data.address.Id);
                }

                const addressRadioButton = this._initRadioButton(
                    `${this.ADDRESS_BUTTON_ID_PREFIX}${editedAddress.Id}`
                );
                if (addressRadioButton && data.address.isAllowed) {
                    addressRadioButton.value(true);
                }
                this._selectFirstCourier(this._selectedDeliveryAddressId);
            }
        }
    }

    private _prepareAddressData(
        address: IAddress,
        deliveryModel: IDeliveryModel
    ): IMultipleCouriersAddressModel | null {
        if (deliveryModel.HomeDeliveryChannels) {
            const channels: IChannelsInfo[] = deliveryModel.HomeDeliveryChannels;

            const newChannels: IChannelsInfo[] = [];
            const currentAddress = deliveryModel.HomeDeliveryAddresses.filter(
                (deliveryAddress) => address.Id === deliveryAddress.Id
            )[0];
            channels.forEach((channel) => {
                currentAddress.HomeDeliveryChannelsInfo.forEach((channelInfo) => {
                    if (channel.DeliveryChannelId === channelInfo.Id && channelInfo.IsAllowed) {
                        const newChannel = {
                            ...channel,
                            IsAllowed: true,
                        };
                        newChannels.push(newChannel);
                    }
                });
            });

            address.isAllowed = newChannels.filter((channel) => channel.IsAllowed).length > 0;

            let outOfStockItems: IOutOfStockItems[] = [];

            if (address.isAllowed) {
                const firstAllowedChannel = newChannels.filter((channel) => channel.IsAllowed)[0];
                outOfStockItems = firstAllowedChannel.OutOfStockItems;
            }

            const multipleCourierAddress: IMultipleCouriersAddressModel = {
                ...address,
                HomeDeliveryChannels: newChannels,
                OutOfStockItems: outOfStockItems,
            };

            return multipleCourierAddress;
        }
        return null;
    }

    private _hideNotAllowedChannels(): void {
        for (const address of this._model.CourierHomeDeliveryAddresses) {
            for (const channel of address.HomeDeliveryChannels) {
                this._toggleChannelDisplay(channel, address.Id);
            }
        }
    }

    private _toggleChannelDisplay(channel: IChannelsInfo, addressId: number): void {
        const courier = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${channel.DeliveryChannelId}"][${this.DATA_ADDRESS}="${addressId}"]`
        );

        if (courier) {
            Utils.toggleClass(courier, this.CLASS_HIDDEN, !channel.IsAllowed);
        }
    }

    private _enableAllowedAddresses(): void {
        const addressList = this.findElement(this.ADDRESS_LIST_CLASS);
        if (!addressList) {
            this._logger.error(`Address list ${this.ADDRESS_LIST_CLASS} was not found.`);
            this._enableHomeDelivery(false);
            return;
        }

        this._model.CourierHomeDeliveryAddresses.forEach((item) => {
            const input = Utils.find(addressList, `input[value=${item.Id}]`)[0];
            if (input) {
                this._enableAllowedAddress(input as HTMLInputElement, Boolean(item.isAllowed));
            }
        });

        if (this._isAddAddressByHomeLabelAllowed) {
            this._enableHomeDelivery(true);
            return;
        }

        const notAllowedAddresses = this._model.CourierHomeDeliveryAddresses.filter(
            (x) => !x.isAllowed
        );
        const isAllowedAddressExist =
            notAllowedAddresses.length !== this._model.CourierHomeDeliveryAddresses.length;

        this._enableHomeDelivery(isAllowedAddressExist);
    }

    private _enableHomeDelivery(isEnabled?: boolean): void {
        const radioButton = kendo.getRadioButton(this.METHOD_RADIO_SELECTOR);
        if (radioButton) {
            radioButton.enable(isEnabled);
        }
    }

    private _enableAllowedAddress(input: HTMLInputElement, isEnabled: boolean): void {
        this._enableAddress(input, isEnabled);
        const couriers = this.find(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}][${this.DATA_ADDRESS}="${input.value}"]`
        );
        if (couriers) {
            couriers.forEach((courier) => {
                if (!isEnabled) {
                    courier.setAttribute(this.DISABLED_ATTRIBUTE, this.DISABLED_ATTRIBUTE);
                } else {
                    courier.removeAttribute(this.DISABLED_ATTRIBUTE);
                }
            });
        }
    }

    private _enableAddress(input: Element, isEnabled: boolean): void {
        const radioButton = kendo.getRadioButton(input);
        if (radioButton) {
            radioButton.enable(isEnabled);
        }
    }

    private _initRadioButtons(): void {
        this._initRadioButton(this.METHOD_RADIO_SELECTOR);

        const addressButtons = this.find(this.ADDRESS_BUTTONS);
        if (addressButtons) {
            for (const addressButton of addressButtons) {
                this._initRadioButton(addressButton);
            }
        }
    }

    private _initDeliveryComment(): void {
        const input = this.findElement(this.DELIVERY_COMMENT);
        if (input) {
            kendo.createTextBox(input);
        }
    }

    @autobind
    private _updateDeliveryComment(): void {
        const input = kendo.getTextBox(this.DELIVERY_COMMENT);
        if (input) {
            const comment = input.value();
            if (comment) {
                const data = new FormData();
                data.append("deliveryComment", comment);
                this._fetchService.post(this._updateDeliveryCommentUrl, data).then(
                    () => {
                        this._logger.info("Delivery comment posted successfully.");
                    },
                    (error) => {
                        this._logger.error("Delivery comment post failed - %s", error);
                        if (ori.shopping) {
                            ori.displayError(
                                this._ajaxErrorTitle,
                                ori.shopping.getAjaxErrorMessage()
                            );
                        } else {
                            ori.displayError(this._ajaxErrorTitle, this._ajaxErrorMessage);
                        }
                    }
                );
            }
        }
    }

    @autobind
    private _deleteDeliveryAddress(address: number | null): void {
        if (!address) {
            this._logger.error("Delivery address not found");
            return;
        }
        const data = new FormData();
        const addressId = address.toString();
        data.append("deliveryAddressId", addressId);
        this._fetchService.post(this._deleteDeliveryAddressUrl, data).then(
            () => {
                this._logger.info("Delivery address successfully deleted.");
                if (!ori.shopping || !ori.shopping || !ori.shopping.wizard) {
                    return;
                }
                const nextAddressId = this.findElementStrict(
                    `${this.ADDRESSES_WRAPPER_SELECTOR} ${this.ADDRESS_ITEM}`
                ).getAttribute("data-address");
                this.findElementStrict(
                    `${this.ADDRESS_ITEM}[data-address='${addressId}']`
                ).remove();
                this._selectAddress(Number(nextAddressId));
            },
            (error) => {
                this._logger.error("Delivery address delete failed - %s", error);
                if (ori.shopping) {
                    ori.displayError(this._ajaxErrorTitle, ori.shopping.getAjaxErrorMessage());
                } else {
                    ori.displayError(this._ajaxErrorTitle, this._ajaxErrorMessage);
                }
            }
        );
    }

    private _bindAddressDeleteClick(): void {
        this.bindDelegatedClick(this.SEL_ADDRESS_DELETE_ACTION, () => {
            this._createAddressDeleteDialog();
            this._openKendoDialog();
        });
    }

    private _openKendoDialog(): void {
        this._addressDeleteDialog.open();
    }

    private _closeKendoDialog(): void {
        this._addressDeleteDialog.close();
    }

    @autobind
    private _createAddressDeleteDialog(): void {
        if (this._isAddressDeleteDialogInited) {
            return;
        }

        const dialogElement = document.createElement("div");
        dialogElement.setAttribute("id", this.ADDRESS_DELETE_DIALOG_ID);
        const dialogOptions: kendo.ui.DialogOptions = {
            closable: false,
        };
        this._addressDeleteDialog = kendo.createDialog(dialogElement, dialogOptions);
        const template = Utils.getTemplate(this.ADDRESS_DELETE_TEMPLATE_ID);
        this._addressDeleteDialog.title(this._addressDeleteDialogTitle).content(template([]));

        const addressDeleteDialogElement = Utils.closest(dialogElement, this.SEL_WINDOW);
        if (addressDeleteDialogElement) {
            Utils.addClass(addressDeleteDialogElement, this.ADDRESS_DELETE_DIALOG_CLASS);
        }

        this._addressDeleteDialog.one("open", () => {
            this._bindKendoDialogEvents();
        });

        this._isAddressDeleteDialogInited = true;
    }

    private _bindKendoDialogEvents(): void {
        const addressDeleteBinder = new EventBinder();
        addressDeleteBinder.init(this.ADDRESS_DELETE_DIALOG);

        // Close popup
        addressDeleteBinder.bindDelegatedClick(this.SEL_CLOSE_ADDRESS_DELETE_DIALOG, () => {
            this._closeKendoDialog();
        });

        // Close popup and remove address
        addressDeleteBinder.bindDelegatedClick(this.SEL_ADDRESS_DELETE_SUBMIT, () => {
            this._deleteDeliveryAddress(this._selectedDeliveryAddressId);
            this._closeKendoDialog();
        });
    }

    private _disableEmptyAddresses(): void {
        const addressList = this.findElement(this.ADDRESS_LIST_CLASS);
        if (!addressList) {
            this._logger.error(`Address list ${this.ADDRESS_LIST_CLASS} was not found.`);
            this._enableHomeDelivery(false);
            return;
        }

        const allAddresses = Utils.find(addressList, this.ADDRESS_ITEM);
        if (allAddresses) {
            const addressesToDisable = allAddresses.filter(
                (address) => !Utils.find(address, this.COURIER_WRAPPER).length
            );

            addressesToDisable.forEach((address) => {
                const addressId = Utils.getData(address, this.ADDRESS_DATA_KEY);
                const input = Utils.find(addressList, `input[value=${addressId}]`)[0];
                if (input) {
                    this._enableAllowedAddress(input as HTMLInputElement, false);
                }
            });

            this._enableHomeDelivery(
                addressesToDisable.length < this._model.CourierHomeDeliveryAddresses.length
            );
        }
    }

    private _toggleFeesVisibility(addressId: number | null, hide: boolean): void {
        const deliveryFees = this.find(
            `li[${this.DATA_ADDRESS}="${addressId}"]
            ${this.VISIBLE_COURIERS} .${this.DELIVERY_FEE_CLASS}`
        );

        if (deliveryFees) {
            deliveryFees.forEach((fee) => Utils.toggleClass(fee, this.CLASS_HIDDEN, hide));
        }
    }

    private _forceDisplayInvalidAddresses(courierAddresses: IMultipleCouriersAddressModel[]): void {
        if (!courierAddresses.length) {
            return;
        }

        const addressesWrapper = this.findElement(`${this.ADDRESSES_WRAPPER_SELECTOR}`);
        if (!addressesWrapper) {
            return;
        }

        let isOtherComponentVisible = false;
        const allowedAddresses = courierAddresses.filter((x) => x.isAllowed);
        if (allowedAddresses.length) {
            addressesWrapper.removeAttribute(this.ACTIVE_HIDDEN_DATA_ATTRIBUTE);
            addressesWrapper.setAttribute(this.ACTIVE_SHOWN_DATA_ATTRIBUTE, "");
            isOtherComponentVisible = true;
        } else {
            addressesWrapper.removeAttribute(this.ACTIVE_SHOWN_DATA_ATTRIBUTE);
            addressesWrapper.setAttribute(this.ACTIVE_HIDDEN_DATA_ATTRIBUTE, "");
        }
        this._toggleDeliveryCommentVisibility(isOtherComponentVisible);
        this._toggleNotificationsPhonesVisibility(isOtherComponentVisible);
    }

    private _toggleDeliveryCommentVisibility(isVisible: boolean): void {
        const deliveryComment = this.findElement(`${this.DELIVERY_COMMENT_WRAPPER_SELECTOR}`);
        if (deliveryComment) {
            Utils.toggleClass(deliveryComment, this.CLASS_HIDDEN, !isVisible);
        }
    }

    private _toggleNotificationsPhonesVisibility(isVisible: boolean): void {
        if (this._deliveryNotificationsPhone) {
            const notificationsPhonesComponent = this.findElement(
                `${this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR}`
            );
            if (notificationsPhonesComponent) {
                Utils.toggleClass(notificationsPhonesComponent, this.CLASS_HIDDEN, !isVisible);
            }
        }
    }

    private _toggleAddAddressButtonVisibility(isAddButtonShown: boolean): void {
        const addAddressButton = this.findElement(this.ADDRESS_ADD_SELECTOR);
        if (addAddressButton) {
            addAddressButton.classList.toggle(this.CLASS_HIDDEN, !isAddButtonShown);
        }
    }

    private _isHomeDeliveryEnabled(): boolean {
        const radioButtonElement = this.findElement(this.METHOD_RADIO_SELECTOR);
        if (radioButtonElement) {
            const disabledAttribute = Utils.getAttr(radioButtonElement, this.DISABLED_ATTRIBUTE);
            return !disabledAttribute;
        }
        return false;
    }

    private _isHomeDeliverySelected(): boolean {
        const homeDeliveryInput = this.findElement(this.METHOD_RADIO_SELECTOR);
        if (homeDeliveryInput) {
            const radioButton = kendo.getRadioButton(homeDeliveryInput);
            if (radioButton) {
                return radioButton.value();
            }
        }
        return false;
    }
}
